import { router } from 'expo-router';
import { useAtomValue } from 'jotai';
import React from 'react';
import {
    ArrowRightIcon,
    BadgeLabel,
    CheckIcon,
    EditIcon,
    Icon,
    LinkIcon,
    ListChecksIcon,
    ReceiptIcon,
    Row,
    RowList,
    UsersRoundIcon,
    View,
    YStack,
} from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { SIDEBAR_CONTENT_MAX_WIDTH } from '../../common/utils/homeUtils';
import { hasAnyExternalHolisticAccountsAtom } from '../../holistic/state/HolisticAtoms';
import { usePreMoneyMapCompleteness } from '../hooks/usePreMoneyMapCompleteness';

const CompletedCheckIcon = () => (
    <View
        backgroundColor="$green900"
        width={26}
        height={26}
        borderRadius={16}
        alignItems="center"
        justifyContent="center"
    >
        <Icon icon={CheckIcon} color="$white" size={14} />
    </View>
);

interface BeforeMoneyMapSessionProps {
    shouldRenderHeader: boolean;
}

export const BeforeMoneyMapSession = ({
    shouldRenderHeader,
}: BeforeMoneyMapSessionProps) => {
    const user = useAuthedUser();
    const hasAnyExternalHolisticAccounts = useAtomValue(
        hasAnyExternalHolisticAccountsAtom,
    );

    const {
        isBillsComplete,
        hasViewedLinkMoreAccounts,
        isFinancialProfileComplete,
    } = usePreMoneyMapCompleteness();

    const shouldShowInvitePartner =
        user.memberJourneyLog?.mentionedPartnerInOnboardingChat === true &&
        user.partnerMember == null;

    return (
        <YStack
            $sm={{
                width: '100%',
                maxWidth: SIDEBAR_CONTENT_MAX_WIDTH,
                alignSelf: 'center',
            }}
        >
            <RowList
                rowHeaderProps={
                    shouldRenderHeader
                        ? {
                              title: 'Complete your profile',
                          }
                        : undefined
                }
            >
                {shouldShowInvitePartner && (
                    <Row
                        containerProps={{
                            paddingVertical: 16,
                        }}
                        title="Invite your partner"
                        subtitle="It's free and easier to manage"
                        leftIcon={UsersRoundIcon}
                        onPress={() => {
                            router.navigate('/settings/add-partner');
                        }}
                        rightContent={
                            <Icon icon={ArrowRightIcon} color="$black" />
                        }
                    />
                )}
                <Row
                    containerProps={{
                        paddingVertical: 16,
                    }}
                    title="Complete financial profile"
                    subtitle="Add income and other details"
                    leftIcon={ListChecksIcon}
                    onPress={() => {
                        router.navigate('/profile/financial-profile');
                    }}
                    leftContentOverride={
                        isFinancialProfileComplete ? (
                            <CompletedCheckIcon />
                        ) : undefined
                    }
                    rightContent={
                        isFinancialProfileComplete ? (
                            <Icon icon={ArrowRightIcon} color="$black" />
                        ) : (
                            <BadgeLabel
                                size="large"
                                variant="warning"
                                text="Finish"
                                icon={EditIcon}
                            />
                        )
                    }
                />
                <Row
                    containerProps={{
                        paddingVertical: 16,
                    }}
                    title="Confirm your bills"
                    subtitle="Review monthly expenses"
                    leftIcon={ReceiptIcon}
                    onPress={() => {
                        if (!hasAnyExternalHolisticAccounts) {
                            router.navigate('/onboarding/expenses-explainer');
                        } else {
                            const routeToNavigateTo = user.spinwheelId
                                ? '/onboarding/expenses/bills-processing'
                                : '/onboarding/expenses/disclaimer';
                            router.navigate({
                                pathname: routeToNavigateTo,
                                params: { source: 'before-money-map-session' },
                            });
                        }
                    }}
                    leftContentOverride={
                        isBillsComplete ? <CompletedCheckIcon /> : undefined
                    }
                    rightContent={
                        isBillsComplete ? (
                            <Icon icon={ArrowRightIcon} color="$black" />
                        ) : (
                            <BadgeLabel
                                size="large"
                                variant="warning"
                                text="Finish"
                                icon={EditIcon}
                            />
                        )
                    }
                />
                <Row
                    containerProps={{
                        paddingVertical: 16,
                    }}
                    title="Link more accounts"
                    subtitle="Savings & investment accounts"
                    leftIcon={LinkIcon}
                    onPress={() => {
                        router.navigate('/profile/external-accounts');
                    }}
                    leftContentOverride={
                        hasViewedLinkMoreAccounts ? (
                            <CompletedCheckIcon />
                        ) : undefined
                    }
                    rightContent={
                        hasViewedLinkMoreAccounts ? (
                            <Icon icon={ArrowRightIcon} color="$black" />
                        ) : (
                            <BadgeLabel
                                size="large"
                                variant="warning"
                                text="Link"
                                icon={LinkIcon}
                            />
                        )
                    }
                />
            </RowList>
        </YStack>
    );
};
